import { useEffect, useState, useCallback } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { ControllerCrearClienteModulo, ControllerEditarCliente } from "../../Service/Service.cliente";
import { ListarTipoDocumento } from "../../Service/Service.tipoDocumento";
import SelectSearchable from "../SelectSeachble/SelectSearchble";
import { GetTokenDecoded } from "../../utils/storeUsuario";

// CLIENTES-MODULO-01: formulario ÚNICO reutilizado para CREAR y EDITAR un cliente
// de facturación. `es_juridica` se elige explícitamente (Natural / Jurídica);
// NUNCA se infiere del tipo de documento (un RUC de persona natural es válido).
const VACIO = {
    nombre: "", tipo_documento: "", identificacion: "", es_juridica: false,
    direccion: "", correo: "", celular: "", telefono: "",
};

// codigo del tipo de documento -> longitud esperada de la identificación
const LARGO_ESPERADO = { C: 10, R: 13 };

function ModalCliente({ data }) {
    const { show, handleClose, cliente } = data; // cliente = null (crear) | objeto (editar)
    const esEdicion = !!(cliente && cliente.idcliente);

    const [form, setForm] = useState(VACIO);
    const [tipos, setTipos] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const cargarTipos = useCallback(async () => {
        const r = await ListarTipoDocumento();
        if (r && r.success) setTipos((r.data || []).map((t) => ({ value: t.idtipodocumento, label: t.tipo, codigo: t.codigo })));
    }, []);

    useEffect(() => {
        if (!show) return;
        setError("");
        cargarTipos();
        if (esEdicion) {
            setForm({
                nombre: cliente.nombre ?? "",
                tipo_documento: cliente.tipo_documento ?? "",
                identificacion: cliente.identificacion ?? "",
                es_juridica: Number(cliente.es_juridica) === 1,
                direccion: cliente.direccion ?? "",
                correo: cliente.correo ?? "",
                celular: cliente.celular ?? "",
                telefono: cliente.telefono ?? "",
            });
        } else {
            setForm(VACIO);
        }
    }, [show, cliente, esEdicion, cargarTipos]);

    const codigoSel = tipos.find((t) => t.value === form.tipo_documento)?.codigo;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validar = () => {
        if (!form.nombre.trim()) return "El nombre / razón social es obligatorio.";
        if (!form.tipo_documento) return "Selecciona el tipo de documento.";
        const id = form.identificacion.trim();
        if (!id) return "La identificación es obligatoria.";
        const largo = LARGO_ESPERADO[codigoSel];
        if (largo && !/^\d+$/.test(id)) return codigoSel === "R" ? "El RUC debe ser numérico." : "La cédula debe ser numérica.";
        if (largo && id.length !== largo) return codigoSel === "R" ? "El RUC debe tener 13 dígitos." : "La cédula debe tener 10 dígitos.";
        if (!form.direccion.trim()) return "La dirección es obligatoria.";
        if (!form.correo.trim()) return "El correo es obligatorio.";
        return "";
    };

    const guardar = async () => {
        if (saving) return;
        const msg = validar();
        if (msg) { setError(msg); return; }
        setError("");
        setSaving(true);

        const payload = {
            nombre: form.nombre.trim(),
            tipo_documento: form.tipo_documento,
            identificacion: form.identificacion.trim(),
            es_juridica: form.es_juridica ? 1 : 0,
            direccion: form.direccion.trim(),
            correo: form.correo.trim(),
            celular: form.celular.trim(),
            telefono: form.telefono.trim(),
        };

        const r = esEdicion
            ? await ControllerEditarCliente(cliente.idcliente, payload)
            : await ControllerCrearClienteModulo({ ...payload, idclinica: GetTokenDecoded()?.idclinica });
        setSaving(false);

        const ok = r && (r.success === true || r.status === 200);
        if (ok) {
            Swal.fire({ icon: "success", title: esEdicion ? "Cliente actualizado" : "Cliente creado", timer: 1500, showConfirmButton: false });
            handleClose(true);
            return;
        }
        if (r && (r.status === 409 || /ya existe|ya usa/i.test(r.message || ""))) {
            setError("Ya existe un cliente con esa identificación en esta clínica.");
            return;
        }
        setError(r?.message || "No se pudo guardar el cliente.");
    };

    return (
        <Modal show={show} onHide={() => handleClose(false)} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{esEdicion ? "Editar cliente" : "Nuevo cliente"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-3">
                    <div className="col-12">
                        <label className="form-label mb-1">Nombre / Razón social *</label>
                        <input type="text" className="form-control" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label mb-1">Tipo de documento *</label>
                        <SelectSearchable
                            listCompany={tipos}
                            handleChange={(o) => set("tipo_documento", o?.value ?? "")}
                            selectedOption={tipos.find((t) => t.value === form.tipo_documento) || null}
                            placeholder="Seleccione"
                            isClearable={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label mb-1">Identificación *</label>
                        <input type="text" className="form-control" value={form.identificacion} onChange={(e) => set("identificacion", e.target.value)} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label mb-1">Persona</label>
                        <div className="d-flex gap-3 pt-2">
                            <label className="form-check-label">
                                <input type="radio" className="form-check-input me-1" name="es_juridica" checked={!form.es_juridica} onChange={() => set("es_juridica", false)} />
                                Natural
                            </label>
                            <label className="form-check-label">
                                <input type="radio" className="form-check-input me-1" name="es_juridica" checked={form.es_juridica} onChange={() => set("es_juridica", true)} />
                                Jurídica
                            </label>
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="form-label mb-1">Dirección *</label>
                        <input type="text" className="form-control" value={form.direccion} onChange={(e) => set("direccion", e.target.value)} />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label mb-1">Correo *</label>
                        <input type="text" className="form-control" value={form.correo} onChange={(e) => set("correo", e.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label mb-1">Celular</label>
                        <input type="text" className="form-control" value={form.celular} onChange={(e) => set("celular", e.target.value)} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label mb-1">Teléfono</label>
                        <input type="text" className="form-control" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} />
                    </div>
                </div>
                {error && <div className="text-danger small mt-3">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={() => handleClose(false)}>Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={guardar}>
                    {saving ? <><Spinner as="span" animation="grow" size="sm" /> Por favor espere…</> : (esEdicion ? "Guardar" : "Crear")}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalCliente;
